<template>
  <l-map
    :center="mapCenter"
    :zoom="mapZoom"
    :options="mapOptions"
    class="map"
    ref="map"
    @update:zoom="zoomUpdated"
    @update:center="centerUpdated"
    @ready="onMapReady"
  >
    <l-tile-layer :url="url" :options="tileOptions"> </l-tile-layer>
    <enseigne
      v-for="marker in markersFiltered"
      :key="context + '-' + marker.id"
      :marker="marker"
      :zoom-current="zoomCurrent"
    >
    </enseigne>
    <div>
      <template v-for="centrale in centralesFiltered">
        <div v-if="centrale.polygon.length > 1" :key="centrale.id">
          <template v-for="(poly, index) in centrale.polygon">
            <l-polygon
              :name="centrale.name"
              :fill-opacity="0.5"
              :lat-lngs="poly.polygon.latlngs"
              :key="index"
              :color="poly.polygon.color"
              :fill-color="poly.polygon.fillColor"
            >
            </l-polygon>
          </template>
        </div>
        <div v-else :key="centrale.id">
          <l-polygon
            :name="centrale.name"
            :fill-opacity="0.5"
            :lat-lngs="centrale.polygon.latlngs"
            :key="centrale.id"
            :color="centrale.polygon.color"
            :fill-color="centrale.polygon.fillColor"
          >
          </l-polygon>
        </div>
      </template>
    </div>
  </l-map>
</template>

<script>
import L from "leaflet";
import { LMap, LTileLayer, LPolygon } from "vue2-leaflet";
import { mapState } from "vuex";
import Enseigne from "./Enseigne";
import coordsPoissonnerie from "../assets/coords_poissonnerie.json";
import centrales from "../assets/centrales.json";
import "leaflet/dist/leaflet.css";
import "../leaflet/smoothWheelZoom";

// Durée des vols programmés (sélection, retour France)
const FLY_DURATION = 1.1;
// Décalage vertical (px) du centre à l'arrivée sur un magasin : le popup
// s'ouvre au-dessus du marqueur, on laisse de la place pour qu'il soit
// entièrement visible sans autoPan
const POPUP_OFFSET_PX = 90;
const STORE_ZOOM = 10;

export default {
  components: {
    LMap,
    LTileLayer,
    LPolygon,
    Enseigne,
  },
  data() {
    return {
      url: "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
      // Position courante de la carte, uniquement alimentée par ses propres
      // événements : vue2-leaflet ne déclenche alors jamais de panTo/setZoom
      // concurrent de nos flyTo
      mapCenter: this.$store.state.enseignes.center,
      mapZoom: this.$store.state.enseignes.zoomLevel,
      mapOptions: {
        minZoom: 5,
        maxZoom: 18,
        // Zoom fractionnaire : le pincement et la molette s'arrêtent là où
        // le geste s'arrête, sans recaler sur un niveau entier
        zoomSnap: 0,
        zoomDelta: 1,
        // Molette gérée par smoothWheelZoom (continu)
        scrollWheelZoom: false,
        smoothWheelZoom: true,
        smoothSensitivity: 3,
        // Glissé plus long après le relâché
        inertia: true,
        inertiaDeceleration: 2500,
        inertiaMaxSpeed: 3000,
        easeLinearity: 0.2,
        bounceAtZoomLimits: false,
        zoomAnimation: true,
        markerZoomAnimation: true,
        fadeAnimation: true,
        // Un tap dans le vide ne ferme pas le popup (géré par la croix)
        closePopupOnClick: false,
      },
      tileOptions: {
        maxZoom: 18,
        // Garde une couronne de tuiles autour de l'écran : moins de trous
        // pendant les déplacements rapides
        keepBuffer: 4,
        updateWhenIdle: false,
        updateWhenZooming: true,
        updateInterval: 100,
      },
    };
  },
  computed: {
    ...mapState({
      center: (state) => state.enseignes.center,
      enseignes: (state) => state.enseignes.all,
      centrales: (state) => state.enseignes.centrales,
      centraleType: (state) => state.enseignes.centraleType,
      selectedEnseigne: (state) => state.enseignes.current,
      zoomLevel: (state) => state.enseignes.zoomLevel,
      enseigneFilters: (state) => state.enseignes.enseigneFilters,
      context: (state) => state.enseignes.context,
      viewResetToken: (state) => state.enseignes.viewResetToken,
    }),
    centralesFiltered() {
      return this.centrales.filter(
        (type) => type.centraleType === this.centraleType
      );
    },
    // Filtrage centralisé dans le store : la carte et le compteur affichent
    // exactement le même ensemble de magasins
    markersFiltered() {
      return this.$store.getters.filteredEnseignes;
    },
    zoomCurrent() {
      return this.mapZoom;
    },
  },
  watch: {
    // Sélection d'un magasin (marqueur ou recherche) : un seul vol, puis le
    // popup s'ouvre à l'arrivée s'il ne l'est pas déjà
    selectedEnseigne(newVal) {
      if (!newVal || !newVal.latitude) return;
      this.flyToStore(newVal);
    },
    // Retour à la vue France : désélection d'un magasin ou bouton GPS.
    // Le store a déjà mis à jour center et zoomLevel avant d'incrémenter le jeton.
    viewResetToken() {
      const map = this.leafletMap();
      if (!map) return;
      this.cancelPendingPopup();
      map.flyTo(this.center, this.zoomLevel, { duration: FLY_DURATION });
      this.$store.dispatch("setZoom", false);
    },
  },
  created() {
    // Charger les données de poissonnerie par défaut
    if (this.enseignes.length === 0)
      this.$store.dispatch("setEnseignes", coordsPoissonnerie);

    this.$store.dispatch("setCoords", coordsPoissonnerie);
    this.$store.dispatch("setCentrales", centrales);
  },
  beforeDestroy() {
    this.cancelPendingPopup();
  },
  methods: {
    leafletMap() {
      return this.$refs.map && this.$refs.map.mapObject;
    },
    onMapReady(map) {
      // Un déplacement manuel pendant un vol programmé interrompt le vol,
      // le popup ne doit alors pas s'ouvrir n'importe où
      map.on("dragstart", this.cancelPendingPopup);
    },
    flyToStore(store) {
      const map = this.leafletMap();
      if (!map) return;
      const latLng = L.latLng(Number(store.latitude), Number(store.longitude));
      const center = map.unproject(
        map.project(latLng, STORE_ZOOM).subtract([0, POPUP_OFFSET_PX]),
        STORE_ZOOM
      );

      this.cancelPendingPopup();
      this.pendingPopupId = store.id;
      map.once("moveend", this.openPendingPopup);
      map.flyTo(center, STORE_ZOOM, { duration: FLY_DURATION });

      this.$store.dispatch("setZoomLevel", STORE_ZOOM);
      this.$store.dispatch("setZoom", true);
    },
    openPendingPopup() {
      const map = this.leafletMap();
      const id = this.pendingPopupId;
      this.pendingPopupId = null;
      if (!map || id == null) return;
      if (!this.selectedEnseigne || this.selectedEnseigne.id !== id) return;
      map.eachLayer((layer) => {
        if (
          layer instanceof L.Marker &&
          layer.options.storeId === id &&
          layer.getPopup() &&
          !layer.isPopupOpen()
        ) {
          layer.openPopup();
        }
      });
    },
    cancelPendingPopup() {
      const map = this.leafletMap();
      this.pendingPopupId = null;
      if (map) map.off("moveend", this.openPendingPopup);
    },
    zoomUpdated(newZoom) {
      const zoom =
        typeof newZoom === "number"
          ? newZoom
          : newZoom && newZoom.target
          ? newZoom.target.getZoom()
          : this.mapZoom;
      this.mapZoom = zoom;
      this.$store.dispatch("setZoomLevel", zoom);
      this.$store.dispatch("setZoom", false);
    },
    centerUpdated(newCenter) {
      this.mapCenter = newCenter;
    },
  },
};
</script>

<style>
.map {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

/* Le fond gris de Leaflet se voit entre deux tuiles : on l'accorde au fond
   des tuiles HOT pour que le chargement passe inaperçu */
.leaflet-container {
  background: #e8e4dc;
  font-family: "Roboto", sans-serif;
}

/* Les marqueurs sont des images : on évite tout flou/alias au redimensionnement
   et on les laisse sur leur propre couche de composition */
.leaflet-marker-icon img {
  display: block;
  backface-visibility: hidden;
}

/* Zoom controls */
.leaflet-control-zoom {
  border: none !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.leaflet-control-zoom a {
  width: 36px !important;
  height: 36px !important;
  line-height: 36px !important;
  font-size: 20px !important;
  color: #376374 !important;
  transition: background-color 0.2s ease;
}

.leaflet-control-zoom a:hover {
  background-color: #f5f5f5 !important;
}

/* Sur tactile le pincement suffit, les boutons gênent le pouce */
@media (max-width: 959px) and (pointer: coarse) {
  .leaflet-control-zoom {
    display: none;
  }
}
</style>
